import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { toast } from 'react-toastify'
import { GET_ONE, UPDATE, useClient } from '@peracto/client'
import { Heading, NotFoundMessage, ServerErrorMessage, UnauthorisedMessage } from '@peracto/peracto-ui'

import CategoryForm from './CategoryForm'
import CategoryGenericArticles from './CategoryGenericArticles'
import useCategories from './useCategories'
import { asTree } from './categoryTree'
import { categoryForApi, categoryForForm, categorySchema } from './categoryFields'
import { showApiError } from './apiErrors'

const CategoryEdit = () => {
    const { client } = useClient()
    const { categoryId } = useParams()
    const { categories, error, reload } = useCategories()
    const [category, setCategory] = useState(null)
    const [status, setStatus] = useState('loading')

    const fetchCategory = useCallback(async () => {
        try {
            const { data } = await client(GET_ONE, 'vehicle-categories', {
                id: `vehicle-categories/${categoryId}`,
            })
            setCategory(categoryForForm(data))
            setStatus('loaded')
        } catch (e) {
            setStatus(e.status === 403 ? 'unauthorised' : 'notFound')
        }
    }, [client, categoryId])

    useEffect(() => {
        fetchCategory()
    }, [fetchCategory])

    const onSubmit = async (values, actions) => {
        try {
            await client(UPDATE, 'vehicle-categories', {
                id: `vehicle-categories/${categoryId}`,
                data: categoryForApi(values),
            })
            toast.success('Category saved.')
            actions.setSubmitting(false)
            fetchCategory()
            reload()
        } catch (e) {
            showApiError(e, actions)
        }
    }

    if (error) {
        return <ServerErrorMessage />
    }

    if (status === 'unauthorised') {
        return <UnauthorisedMessage />
    }

    if (status === 'notFound') {
        return (
            <NotFoundMessage
                url="/vehicle-categories"
                message="The category you're looking for could not be found"
                buttonLabel="Go to Vehicle Categories"
            />
        )
    }

    return (
        <>
            <Helmet>
                <title>{category ? category.name : 'Category'} | Vehicle Categories | Peracto</title>
            </Helmet>
            <div className="form-container">
                <Heading name={category ? `Edit ${category.name}` : 'Edit Vehicle Category'}>
                    <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                        <Link className="btn btn-outline-primary" to="/vehicle-categories">
                            <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
                            Back to Vehicle Categories
                        </Link>
                    </div>
                </Heading>
                {status === 'loading' || categories === null ? (
                    <div className="card">
                        <div className="card-body">Loading...</div>
                    </div>
                ) : (
                    <>
                        <CategoryForm
                            modeEdit
                            data={category}
                            tree={asTree(categories)}
                            onSubmit={onSubmit}
                            schema={categorySchema}
                        />
                        <CategoryGenericArticles category={category} />
                    </>
                )}
            </div>
        </>
    )
}

export default CategoryEdit
